import * as React from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

export function RegisterPage() {
  const { register } = useAuth();
  const { toast } = useToast();

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});
  const [registeredEmail, setRegisteredEmail] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFieldErrors({});
    try {
      await register(fullName.trim(), email.trim(), password);
      setRegisteredEmail(email.trim());
      toast({
        title: "Account created",
        description: "Check your inbox to verify your email.",
        variant: "success"
      });
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { errors?: Record<string, string[]>; message?: string };
        if (body.errors) setFieldErrors(body.errors);
        toast({
          title: "Couldn't create account",
          description: body.message ?? err.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Couldn't create account",
          description: err instanceof Error ? err.message : "Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="brand-gradient-bg brand-radial-halo relative min-h-[100dvh] overflow-hidden">
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center px-4 py-10 sm:py-16">
        <Link to="/" className="mb-8 flex items-center">
          <BrandLogo className="h-9 w-auto" />
        </Link>

        <span className="brand-pill-recommended mb-4">Get started</span>
        <h1 className="text-center font-display text-2xl font-bold tracking-tight sm:text-3xl">
          Create your HosterDash account
        </h1>
        <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
          You can add domains, mailboxes and hosting right after sign-up.
        </p>

        <div className="mt-7 w-full">
          {registeredEmail ? (
            <Card className="border-brand-border shadow-brand-md">
              <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold">Check your email</h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                    We've sent a verification link to <strong>{registeredEmail}</strong>. Click it to
                    activate your account — you'll need to verify before you can sign in. Didn't get it?
                    Check your spam folder, or resend below.
                  </p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:flex-row">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to="/verify-email">Resend link</Link>
                  </Button>
                  <Button asChild className="flex-1">
                    <Link to="/login">Go to sign in</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
          <Card className="border-brand-border shadow-brand-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl sm:text-2xl">Sign up</CardTitle>
              <CardDescription>It's free until you pick a plan.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <Field
                  id="fullName"
                  label="Full name"
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={setFullName}
                  errors={fieldErrors.FullName ?? fieldErrors.fullName}
                  required
                />
                <Field
                  id="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={setEmail}
                  errors={fieldErrors.Email ?? fieldErrors.email}
                  required
                  inputMode="email"
                />
                <Field
                  id="password"
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={setPassword}
                  errors={fieldErrors.Password ?? fieldErrors.password}
                  required
                  hint="At least 8 characters."
                />
                <Button type="submit" disabled={submitting} className="w-full" size="lg">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create account
                </Button>
              </form>
            </CardContent>
          </Card>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type: string;
  autoComplete?: string;
  value: string;
  onChange: (next: string) => void;
  errors?: string[];
  required?: boolean;
  hint?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

function Field({ id, label, type, autoComplete, value, onChange, errors, required, hint, inputMode }: FieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-invalid={errors && errors.length > 0}
      />
      {errors && errors.length > 0 ? (
        <p className="text-xs text-destructive">{errors[0]}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
